import React, { useEffect, useState, useRef, useCallback } from "react";
import { Menu } from "lucide-react";
import Sidebar from "../Sidebar/sidebar";
import { useLanguage } from "../../context/LanguageContext";

export default function MainLayout({ children }) {
  const { isIndonesian, isEnglish, toggleLanguage, t } = useLanguage();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [navHidden, setNavHidden] = useState(false);
  const lastScrollY = useRef(0);
  const contentRef = useRef(null);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setMobileSidebarOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  useEffect(() => {
    if (!mobileSidebarOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileSidebarOpen]);

  // Auto-hide navbar on scroll down, show on scroll up
  const handleScroll = useCallback((e) => {
    const el = e.target;
    if (!el || typeof el.scrollTop !== 'number') return;
    const currentY = el.scrollTop;
    if (currentY > lastScrollY.current && currentY > 60) {
      setNavHidden(true);
    } else {
      setNavHidden(false);
    }
    lastScrollY.current = currentY;
  }, []);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    // Use capture phase to catch scroll from ANY nested scrollable element
    el.addEventListener("scroll", handleScroll, { passive: true, capture: true });
    return () => el.removeEventListener("scroll", handleScroll, { capture: true });
  }, [handleScroll]);

  return (
    <div className="flex h-[100dvh] overflow-hidden bg-paper">
      <div className="hidden h-full shrink-0 md:block">
        <Sidebar />
      </div>

      <div
        className={`fixed inset-0 z-40 md:hidden ${
          mobileSidebarOpen ? "pointer-events-auto" : "pointer-events-none"
        }`}
      >
        <div
          className={`absolute inset-0 bg-slate-950/50 backdrop-blur-[2px] transition-opacity duration-300 ${
            mobileSidebarOpen ? "opacity-100" : "opacity-0"
          }`}
          onClick={() => setMobileSidebarOpen(false)}
        />

        <div
          className={`absolute inset-y-0 left-0 transition-transform duration-300 ${
            mobileSidebarOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <Sidebar
            mobile
            onClose={() => setMobileSidebarOpen(false)}
            onNavigate={() => setMobileSidebarOpen(false)}
          />
        </div>
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className={`flex items-center justify-between px-4 backdrop-blur md:hidden transition-all duration-300 ease-in-out ${navHidden ? 'max-h-0 py-0 border-0 overflow-hidden opacity-0 pointer-events-none' : 'max-h-16 py-3 border-b border-ledger-border bg-paper/95 opacity-100'}`}>
          <button
            type="button"
            onClick={() => setMobileSidebarOpen(true)}
            className="rounded-xl border border-ledger-border bg-card-bg p-2 text-text-sec shadow-sm transition hover:bg-surface-hover"
            aria-label="Open navigation"
          >
            <Menu className="h-5 w-5" />
          </button>

          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand text-sm font-bold text-white shadow-lg shadow-brand/30">
              AI
            </div>
            <span className="text-sm font-semibold tracking-tight text-text-pri">
              Tacia
            </span>
          </div>

          <button
            type="button"
            onClick={toggleLanguage}
            className="flex items-center gap-1 rounded-xl border border-ledger-border bg-card-bg px-2.5 py-1.5 text-xs font-bold tracking-wider text-text-sec shadow-sm transition hover:bg-surface-hover"
            title={t("sidebar.langSwitchTooltip")}
          >
            <span className={isIndonesian ? "text-brand font-extrabold" : "opacity-40"}>ID</span>
            <span className="opacity-30">|</span>
            <span className={isEnglish ? "text-brand font-extrabold" : "opacity-40"}>EN</span>
          </button>
        </div>

        <div ref={contentRef} className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain flex flex-col">
          {children}
        </div>
      </div>
    </div>
  );
}

